import React, {useMemo} from "react"
import {Bar} from "react-chartjs-2"
import {BarElement, CategoryScale, Chart, Legend, LinearScale, Title, Tooltip} from "chart.js"

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const RIGHT_LEGEND_MAX_CHARS = 28
const TRANSPARENT = "rgba(0,0,0,0)"

const wrapText = (text, maxChars) => {
    const words = String(text ?? "").split(/\s+/).filter(Boolean)
    if (words.length === 0) return [""]
    const lines = []
    let current = ""
    for (const word of words) {
        if (!current) {
            current = word
        } else if ((current + " " + word).length <= maxChars) {
            current += " " + word
        } else {
            lines.push(current)
            current = word
        }
    }
    if (current) lines.push(current)
    return lines
}

const resolveColor = (color) => Array.isArray(color) ? color[0] : color

const hasRenderedValues = (ds) =>
    Array.isArray(ds?.data) && ds.data.some((v) => Number.isFinite(+v) && +v !== 0)

const wrappedRightLegendLabels = (chart) => {
    const items = []
    chart.data.datasets.forEach((ds, datasetIndex) => {
        if (!hasRenderedValues(ds)) return
        const lines = wrapText(ds.label, RIGHT_LEGEND_MAX_CHARS)
        const visible = chart.isDatasetVisible(datasetIndex)
        const color = resolveColor(ds.backgroundColor)
        lines.forEach((line, lineIdx) => {
            const isFirst = lineIdx === 0
            items.push({
                text: line,
                fillStyle: isFirst ? color : TRANSPARENT,
                strokeStyle: isFirst ? color : TRANSPARENT,
                lineWidth: 0,
                pointStyle: "circle",
                hidden: !visible,
                datasetIndex,
            })
        })
    })
    return items
}

export const versorgungslueckenVerlaufChartPrototype = [
    {
        "key": "showVerticalTicks",
        "type": "YESNO",
        "label": "Y-Achse anzeigen",
        "size": 6
    },
    {
        key: "legend",
        type: "SELECT",
        label: "Legende",
        size: 6,
        options: [
            {value: "NONE", label: "Keine Legende"},
            {value: "RIGHT", label: "Legende rechts"},
            {value: "BOTTOM", label: "Legende unten"},
        ],
    },
    {
        key: "xAxisTitle",
        type: "STRING",
        label: "X-Achsen-Titel",
        size: 6,
    },
]

const legendPositionMap = {
    NONE: {display: false},
    RIGHT: {
        display: true,
        position: "right",
        align: "end",
        labels: {
            usePointStyle: true,
            pointStyle: "circle",
            generateLabels: wrappedRightLegendLabels,
        },
    },
    BOTTOM: {
        display: true,
        position: "bottom",
        align: "start",
        labels: {
            usePointStyle: true,
            pointStyle: "circle",
        },
    },
}

export const options = ({showVerticalTicks, legend, xAxisTitle}, highlightIndex) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: legendPositionMap[legend] ?? legendPositionMap.NONE,
        tooltip: {
            bodyAlign: "center",
            bodyFont: {
                family: "Urbanist",
                weight: "bold",
                size: 14,
            },
            callbacks: {
                label: (context) => {
                    const datasetLabel = context.dataset.label ?? ""
                    const value = context.raw

                    return `${datasetLabel}: ${value.toLocaleString('de-DE', {minimumFractionDigits: 2})} €`
                },
                afterBody: (context) => {
                    if (highlightIndex >= 0 && context[0]?.dataIndex === highlightIndex) {
                        return "\nStatistische Lebenserwartung zum Zeitpunkt\ndes Renteneintritts."
                    }
                    return ""
                },
            },
        },
    },
    datasets: {
        bar: {
            borderRadius: 4,
        },
    },
    scales: {
        x: {
            display: false,
            barThickness: "48px",
            border: {
                display: false,
            },
            position: "bottom",
            stacked: true,
            grid: {
                display: false,
            },
            ticks: {
                font: {
                    family: "Urbanist",
                    weight: "bold",
                    size: 14,
                },
                minRotation: 0,
                maxRotation: 0,
            },
        },
        y: {
            border: {
                display: false,
            },
            stacked: true,
            grid: {
                display: false,
                color: (context) => {
                    if (context.tick && context.tick.value === 0) {
                        return "#A5AFB8"
                    }
                    return "rgba(0, 0, 0, 0)"
                },
            },
            ticks: {
                display: showVerticalTicks ?? false,
            },
        },
        titles: {
            border: {
                display: false,
            },
            stacked: true,
            position: "bottom",
            grid: {
                display: false,
            },
            title: xAxisTitle ? {
                display: true,
                text: xAxisTitle,
                font: {
                    family: "Urbanist",
                    weight: "bold",
                    size: 14,
                },
            } : {display: false},
            ticks: {
                font: {
                    family: "Urbanist",
                    weight: "bold",
                    size: 14,
                },
            },
        },
    },
})

const fallbackData = {
    datasets: [],
    labels: [],
}

const isChartConfiguration = (value) =>
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Array.isArray(value.datasets)

export const VersorgungslueckenVerlaufChart = ({widget, getDataFromPointer}) => {
    const pointer = useMemo(
        () => widget?.connections?.origin?.pointer,
        [widget],
    )
    const dataFromPointer = getDataFromPointer(pointer)
    const data = isChartConfiguration(dataFromPointer)
        ? dataFromPointer
        : fallbackData
    return <div style={{height: "500px", width: "100%", position: "relative"}}>
        <Bar
            data={data}
            options={options(widget, data.highlightIndex ?? -1)}
        />
    </div>
}